import { globalConfig } from "config";
import React, { useEffect, useRef } from "react";

import type { TurnstileWidgetProps } from "./turnstileWidget.types";

import styles from "./TurnstileWidget.module.css";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile";

/**
 * The Cloudflare Turnstile challenge, rendered on sign-up forms.
 *
 * Unlike the honeypot beside it, this targets the automation rather than the network it arrives on —
 * which is the point, since a bot driving a real browser rotates its address freely but still has to
 * get past the challenge.
 *
 * Renders nothing at all when no site key is configured, and the API treats an absent token as "not
 * checked" rather than refusing the sign-up. A local or preview build therefore works untouched
 * instead of locking everyone out of registering.
 */
const TurnstileWidget = ({ onTokenChange }: TurnstileWidgetProps) => {
	const containerRef = useRef<HTMLDivElement>(null);

	// Held in a ref so a parent that passes an inline callback cannot tear the widget down and
	// re-render it on every keystroke elsewhere in the form.
	const onTokenChangeRef = useRef(onTokenChange);

	useEffect(() => {
		onTokenChangeRef.current = onTokenChange;
	}, [onTokenChange]);

	useEffect(() => {
		const siteKey = globalConfig.turnstileSiteKey;

		if (!siteKey) {
			return undefined;
		}

		let widgetId: string | undefined;

		const renderWidget = (): void => {
			if (!containerRef.current || !window.turnstile || widgetId !== undefined) {
				return;
			}

			widgetId = window.turnstile.render(containerRef.current, {
				sitekey: siteKey,
				callback: (token: string) => onTokenChangeRef.current(token),
				// A token is single-use and short-lived. Clearing it on expiry means a form left open
				// submits nothing rather than something Cloudflare will reject.
				"expired-callback": () => onTokenChangeRef.current(""),
				"error-callback": () => onTokenChangeRef.current("")
			});
		};

		if (window.turnstile) {
			renderWidget();

			return () => {
				if (widgetId !== undefined) {
					window.turnstile?.remove(widgetId);
				}
			};
		}

		// Shared across any form that renders a challenge; load it once and render on load.
		let script = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;

		if (!script) {
			script = document.createElement("script");
			script.id = TURNSTILE_SCRIPT_ID;
			script.src = TURNSTILE_SCRIPT_SRC;
			script.async = true;
			document.head.appendChild(script);
		}

		script.addEventListener("load", renderWidget);

		return () => {
			script?.removeEventListener("load", renderWidget);

			if (widgetId !== undefined) {
				window.turnstile?.remove(widgetId);
			}
		};
	}, []);

	if (!globalConfig.turnstileSiteKey) {
		return null;
	}

	return <div ref={containerRef} className={styles.widget} />;
};

export default TurnstileWidget;
